import { RestError } from '@azure/core-http';
import {
  CreateHubDto,
  EditHubDto,
  EditLockersHubSetupDto,
  HubDetailsDto,
  InvokeAccessControlResponseDto,
  AttachNoteDto,
  HubDto,
  HubOpenDoorsDto,
  HubSummaryDto,
  HubStatus,
  InvokeAccessControlDto,
  KnownSortOrder,
  OpenAllDoorsAsyncAcceptedDto,
  OpenAllDoorsAsyncStatusDto,
  OpenAllResponseDto,
  PaginatedDto,
  DeliveryContainerDto,
  SetupExternalLockersHubDto,
  SetupLockersHubDto
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useApi } from '../../core/api/api.context';
import { fetchAllPages, REFERENCE_LIST_STALE_TIME } from '../../core/queries/query-helpers';
import { QueryKeys } from '../../core/queries/query-keys.constants';
import { HubDetails } from './models/hub-details.model';
import { DeliveryContainerContentsModel } from './models/delivery-container-contents.model';
import { DeliveryContainerHistoricalContentsModel } from './models/delivery-container-historical-content.model';

export function useMutationOpenAllHub(hubID: string) {
  const api = useApi();

  return useMutation<OpenAllResponseDto, RestError, HubOpenDoorsDto>(async (dto) => {
    return await api.hubsController.openDoors(hubID, dto);
  });
}

export function useMutationOpenAllHubAsync(hubID: string) {
  const api = useApi();

  return useMutation<OpenAllDoorsAsyncAcceptedDto, RestError, HubOpenDoorsDto>(async (dto) => {
    return await api.hubsController.openAllDoorsAsync(hubID, dto);
  });
}

export function useMutationGetOpenAllHubStatus(hubID: string) {
  const api = useApi();

  return useMutation<OpenAllDoorsAsyncStatusDto, RestError, string>(async (requestID) => {
    return await api.hubsController.getOpenAllDoorsStatus(hubID, requestID);
  });
}

export function useMutationUpdateHubStatus() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    HubDto,
    RestError,
    {
      hubID: string;
      enable: boolean;
      note: AttachNoteDto;
    }
  >(
    async (request) => {
      const { hubID, ...rest } = request;
      const response = await api.hubsController.enable(hubID, rest);
      return response;
    },
    {
      onSettled(data) {
        queryClient.invalidateQueries([QueryKeys.HUBS_DETAILS]);
        queryClient.invalidateQueries([QueryKeys.HUBS, data?.managedByProjectID]);
      }
    }
  );
}

export function useMutationDeleteHub() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    {
      projectID: string;
      hubID: string;
    },
    RestError,
    {
      projectID: string;
      hubID: string;
      note: AttachNoteDto;
    }
  >(
    async (request) => {
      await api.hubsController.delete(request.hubID);
      await api.hubsNotesController.create(request.hubID, {
        content: request.note.content,
        isPrivate: request.note.private
      });
      return request;
    },
    {
      onSettled(response) {
        queryClient.invalidateQueries([QueryKeys.HUBS_DETAILS]);
        queryClient.invalidateQueries([QueryKeys.HUBS, response?.projectID]);
      }
    }
  );
}

export function useMutationInvokeAccessControl() {
  const api = useApi();

  return useMutation<
    InvokeAccessControlResponseDto,
    RestError,
    InvokeAccessControlDto & {
      accessControlID: string;
      note: AttachNoteDto;
    }
  >(async (request) => {
    return await api.accessControlController.invoke(request.accessControlID, {
      action: request.action,
      note: request.note
    });
  });
}

export const useMutationTriggerBulkDataUpdate = () => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<unknown, RestError, { hubID: string }>(
    async ({ hubID }) => {
      await api.hubsController.bulkDataUpdate(hubID);
    },
    {
      onSettled() {
        queryClient.invalidateQueries([QueryKeys.HUBS_DETAILS]);
      }
    }
  );
};

export function useMutationCreateHub() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<HubDto, RestError, { dto: CreateHubDto }>(
    async ({ dto }) => {
      return await api.hubsController.create(dto);
    },
    {
      onSettled(data) {
        queryClient.invalidateQueries([QueryKeys.HUBS_DETAILS]);
        queryClient.invalidateQueries(QueryKeys.SETTINGS);
        if (data?.managedByProjectID) {
          queryClient.invalidateQueries([QueryKeys.HUBS, data.managedByProjectID]);
        }
      }
    }
  );
}

export function useMutationRemoveAccessViaProject() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<void, RestError, { hubID: string; projectID: string }>(
    async ({ hubID, projectID }) => {
      await api.hubsController.removeAccessViaProject(hubID, projectID);
    },
    {
      onSettled() {
        queryClient.invalidateQueries([QueryKeys.HUBS_DETAILS]);
        queryClient.invalidateQueries(QueryKeys.SETTINGS);
      }
    }
  );
}

export function useMutationSetAccessedViaProject() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<HubDto, RestError, { hubID: string; projectID: string }>(
    async ({ hubID, projectID }) => {
      return await api.hubsController.setAccessedViaProject(hubID, projectID);
    },
    {
      onSettled() {
        queryClient.invalidateQueries([QueryKeys.HUBS_DETAILS]);
        queryClient.invalidateQueries(QueryKeys.SETTINGS);
      }
    }
  );
}

export function useMutationDeleteHubSettings() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<void, RestError, { hubID: string }>(
    async ({ hubID }) => {
      await api.hubsController.delete(hubID);
    },
    {
      onSettled() {
        queryClient.invalidateQueries([QueryKeys.HUBS_DETAILS]);
        queryClient.invalidateQueries([QueryKeys.HUBS]);
        queryClient.invalidateQueries(QueryKeys.SETTINGS);
      }
    }
  );
}

export function useMutationUpdateHub() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<HubDto, RestError, { hubID: string; dto: EditHubDto }>(
    async ({ hubID, dto }) => {
      return await api.hubsController.update(hubID, dto);
    },
    {
      onSettled(data) {
        queryClient.invalidateQueries([QueryKeys.HUBS_DETAILS]);
        queryClient.invalidateQueries(QueryKeys.SETTINGS);
        if (data?.managedByProjectID) {
          queryClient.invalidateQueries([QueryKeys.HUBS, data.managedByProjectID]);
        }
      }
    }
  );
}

export function useQueryListHubs(
  projectID: string,
  options?: { includeChildProjects?: boolean; includeAppearsOn?: boolean; status?: HubStatus; enabled?: boolean }
) {
  const api = useApi();
  const { enabled, ...filters } = options || {};

  return useQuery<HubDto[], RestError>(
    [QueryKeys.HUBS, projectID, filters],
    async () => {
      return await fetchAllPages(
        async (pageNo) =>
          await api.hubsController.list({
            projectID,
            includeChildProjects: filters.includeChildProjects,
            includeAppearsOn: filters.includeAppearsOn,
            status: filters.status,
            pageNo,
            perPageRecords: 100
          })
      );
    },
    { enabled: enabled !== false, staleTime: REFERENCE_LIST_STALE_TIME }
  );
}

/** Hub counts, in total and by type, without loading every hub. */
export function useQueryHubSummary(projectID: string, options?: { includeChildProjects?: boolean }) {
  const api = useApi();

  return useQuery<HubSummaryDto, RestError>(
    [QueryKeys.HUBS, projectID, 'summary', options],
    async () => await api.hubsController.summary({ projectID, includeChildProjects: options?.includeChildProjects }),
    { staleTime: REFERENCE_LIST_STALE_TIME }
  );
}

export function useQueryListHubsDetails(
  projectID: string,
  options?: { includeChildProjects?: boolean; includeAppearsOn?: boolean }
) {
  const api = useApi();

  return useQuery<HubDetails[], RestError>([QueryKeys.HUBS_DETAILS, projectID, options], async () => {
    const result = await fetchAllPages(
      async (pageNo) =>
        await api.hubsController.listDetails({
          projectID,
          includeChildProjects: options?.includeChildProjects,
          includeAppearsOn: options?.includeAppearsOn,
          pageNo,
          perPageRecords: 100
        })
    );
    return result.map((hub) => new HubDetails(hub));
  });
}

export function useQueryGetHub(hubID?: string) {
  const api = useApi();

  return useQuery<HubDto | undefined, RestError>(
    [QueryKeys.HUBS, hubID],
    async () => {
      if (!hubID) {
        return undefined;
      }
      return await api.hubsController.get(hubID);
    },
    { enabled: !!hubID }
  );
}

export function useQueryGetHubDetails(hubID?: string) {
  const api = useApi();

  return useQuery<HubDetails | undefined, RestError>(
    [QueryKeys.HUBS_DETAILS, hubID],
    async () => {
      if (!hubID) {
        return undefined;
      }
      const result = await api.hubsController.getDetails(hubID);
      return new HubDetails(result);
    },
    { enabled: !!hubID }
  );
}

export function useQueryListDeliveryContainerContents(deliveryContainerID: string) {
  const api = useApi();

  return useQuery<DeliveryContainerContentsModel[], RestError>(
    [QueryKeys.DELIVERY_CONTAINERS, deliveryContainerID, QueryKeys.DELIVERY_CONTAINER_CONTENTS],
    async () => {
      const result = await api.deliveryContainersController.listDeliveryContainerContents(deliveryContainerID);
      return (result.items || []).map((item) => new DeliveryContainerContentsModel(item));
    }
  );
}

export function useQueryListDeliveryContainerContentHistory(
  deliveryContainerID: string,
  { pageNo, perPageRecords }: { perPageRecords?: number; pageNo?: number }
) {
  const api = useApi();

  return useQuery<PaginatedDto & { items: DeliveryContainerHistoricalContentsModel[] }, RestError>(
    [QueryKeys.DELIVERY_CONTAINERS, deliveryContainerID, QueryKeys.DELIVERY_CONTAINER_CONTENTS_HISTORY],
    async () => {
      const result = await api.deliveryContainersController.listDeliveryContainerContentHistory(deliveryContainerID, {
        perPageRecords,
        pageNo,
        sortBy: 'deliveredTime',
        sortOrder: KnownSortOrder.Desc
      });
      return {
        ...result,
        items: (result.items || []).map((item) => new DeliveryContainerHistoricalContentsModel(item))
      };
    }
  );
}

export function useMutationSetupExternalLockersHub() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<HubDetailsDto, RestError, { dto: SetupExternalLockersHubDto }>(
    async ({ dto }) => {
      return await api.hubsController.setupExternalLockersHub(dto);
    },
    {
      onSettled() {
        queryClient.invalidateQueries([QueryKeys.HUBS_DETAILS]);
        queryClient.invalidateQueries(QueryKeys.SETTINGS);
      }
    }
  );
}

export function useMutationSetupLockersHub() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<HubDetailsDto, RestError, { dto: SetupLockersHubDto }>(
    async ({ dto }) => {
      return await api.hubsController.setupLockersHub(dto);
    },
    {
      onSettled() {
        queryClient.invalidateQueries([QueryKeys.HUBS_DETAILS]);
        queryClient.invalidateQueries(QueryKeys.SETTINGS);
      }
    }
  );
}

export function useMutationUpdateLockersHubSetup() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<HubDetailsDto, RestError, { hubID: string; dto: EditLockersHubSetupDto }>(
    async ({ hubID, dto }) => {
      return await api.hubsController.updateLockersHubSetup(hubID, dto);
    },
    {
      onSettled() {
        queryClient.invalidateQueries([QueryKeys.HUBS_DETAILS]);
        queryClient.invalidateQueries(QueryKeys.SETTINGS);
      }
    }
  );
}

export function useHasMultipleHubs(
  projectID: string,
  options?: { includeChildProjects?: boolean; includeAppearsOn?: boolean }
) {
  const hubsOnThisAndChildProjects = useQueryListHubs(projectID, options);

  // If there is more than 1 hub returned from useQueryListHubs, then there are multiple hubs
  return (hubsOnThisAndChildProjects.data && hubsOnThisAndChildProjects.data.length > 1) || false;
}

export function useQueryGetDeliveryContainer(deliveryContainerID: string) {
  const api = useApi();

  return useQuery<DeliveryContainerDto | undefined, RestError>(
    [QueryKeys.DELIVERY_CONTAINERS, deliveryContainerID],
    async () => {
      return await api.deliveryContainersController.get(deliveryContainerID);
    },
    { enabled: !!deliveryContainerID }
  );
}
